<template>
	<div class="container">
		<h1>Terms of Service</h1>
		<p>
			<strong>CODECANNON TERMS OF SERVICE</strong><br />
			Last Updated: 2025, May 6th
		</p>

		<section id="introduction">
			<h2>1. Introduction</h2>
			<p>
				Welcome to Codecannon (“Service”), a platform developed and operated by Andrej Fidel s.p.
				(“we,” “us,” or “our”). Codecannon leverages various AI models and technologies, including
				but not limited to OpenAI, to provide automated responses to user queries. By accessing or
				using Codecannon, you agree to comply with and be bound by these Terms of Service (“Terms”).
			</p>
			<p>
				If you do not agree with any part of these Terms, you must not access or use Codecannon.
			</p>
		</section>

		<section id="eligibility">
			<h2>2. Eligibility</h2>
			<p>You may only use Codecannon if:</p>
			<ol>
				<li>You are at least the age of majority in your jurisdiction; and</li>
				<li>You have the legal capacity and authority to enter into these Terms.</li>
			</ol>
			<p>
				By using Codecannon, you represent and warrant that you meet all the eligibility criteria
				stated above.
			</p>
		</section>

		<section id="acceptance">
			<h2>3. Acceptance of Terms</h2>
			<p>
				By accessing or using Codecannon, you confirm that you have read, understood, and agree to
				be bound by these Terms and all applicable laws. If you do not agree, you should immediately
				stop using the Service.
			</p>
		</section>

		<section id="modifications">
			<h2>4. Modifications to the Terms</h2>
			<p>
				We reserve the right, in our sole discretion, to change, modify, or update these Terms at
				any time. When we do, we will post the updated Terms on our website or within the Service
				and update the “Last Updated” date at the top of this page. Your continued use of Codecannon
				after any such changes constitute your acceptance of the revised Terms.
			</p>
		</section>

		<section id="description">
			<h2>5. Description of the Service</h2>

			<h3>5.1 General Functionality</h3>
			<p>
				Codecannon is an AI-assisted code generation service that allows users to generate Vue +
				Laravel codebases.
			</p>

			<h3>5.2 Code Outputs</h3>
			<p>
				The outputs from Codecannon are deterministically generated. These outputs might not always
				be correct, bug-free, complete, or up-to-date. You acknowledge that all outputs and
				responses are provided on an “as is” basis.
			</p>

			<h3>5.3 Updates and Improvements</h3>
			<p>
				We may, at our discretion and without obligation, update, improve, or change the
				functionality of Codecannon. We do not guarantee that any specific feature or part of the
				Service will always be available.
			</p>
		</section>

		<section id="conduct">
			<h2>6. User Conduct</h2>
			<p>When using Codecannon, you agree <strong>not</strong> to:</p>
			<ul>
				<li>Violate any applicable laws, regulations, or these Terms.</li>
				<li>
					Use the Service for any unlawful, harmful, threatening, abusive, harassing, defamatory, or
					otherwise objectionable purpose.
				</li>
				<li>
					Attempt to gain unauthorized access to, interfere with, or disrupt any servers or networks
					connected to Codecannon.
				</li>
				<li>
					Transmit viruses, malware, or other malicious code that could harm Codecannon, other
					users, or third parties.
				</li>
				<li>Collect or harvest any personal data about other users.</li>
				<li>
					Use Codecannon to generate or share content that infringes on any intellectual property or
					privacy rights of a third party.
				</li>
			</ul>
		</section>

		<section id="privacy">
			<h2>7. Privacy</h2>
			<p>
				Your use of Codecannon is also subject to our Privacy Policy, which describes how we
				collect, use, process, and protect your personal data. By using Codecannon, you consent to
				all actions taken by us concerning your information in compliance with our Privacy Policy.
			</p>
		</section>

		<section id="ip">
			<h2>8. Intellectual Property</h2>
			<h3>Ownership</h3>
			<p>
				Andrej Fidel s.p. and its licensors own all rights, title, and interest in and to
				Codecannon, including any underlying software, technology, and content (excluding User
				Content).
			</p>
			<h3>User Content License</h3>
			<p>
				By submitting or transmitting any content (“User Content”) to Codecannon, you grant Andrej
				Fidel s.p. a non-exclusive, worldwide, royalty-free, sublicensable, and transferable license
				to store, display, process, and use your User Content in connection with providing and
				improving the Service.
			</p>
			<h3>Trademarks</h3>
			<p>
				All trademarks, service marks, logos, trade names, and any other proprietary designations
				belonging to Andrej Fidel s.p. used herein are trademarks of Andrej Fidel s.p.
			</p>
			<h3>Generated applications</h3>
			<p>
				Delivered applications include a "Codecannon Licence" which dictates the terms of use for
				generated applications. We reserve the right to generate identical applications for any
				number of customers.
			</p>
		</section>

		<section id="disclaimers">
			<h2>9. Disclaimers</h2>
			<h3>No Warranty</h3>
			<p>
				The Service is provided on an “as is” and “as available” basis. To the fullest extent
				permitted by applicable law, we disclaim all warranties of any kind, whether express or
				implied, including but not limited to implied warranties of merchantability, fitness for a
				particular purpose, and non-infringement.
			</p>
			<h3>Accuracy of Information</h3>
			<p>
				We do not guarantee the accuracy, completeness, or timeliness of any information, code, or
				data provided by Codecannon. Any reliance on such information is at your own risk.
			</p>
			<h3>Money Back Guarantee</h3>
			<p>
				We offer a 30-day money-back guarantee for all generated applications if you are in any way
				unsatisfied with our product. Should you receive a refund under this guarantee, your license
				for the specific generated application will be terminated, and you will forfeit all rights
				to use its codebase in any way, shape, or form.
			</p>
		</section>

		<section id="liability">
			<h2>10. Limitation of Liability</h2>
			<p>To the fullest extent permitted by applicable law:</p>
			<ol>
				<li>
					<strong>No Liability for Indirect Damages.</strong> Andrej Fidel s.p. will not be liable
					for any indirect, incidental, special, consequential, or punitive damages, including but
					not limited to loss of profits, data, use, or goodwill, arising from or related to your
					use of codecannon.
				</li>
				<li>
					<strong>Aggregate Liability Cap.</strong> In no event will Andrej Fidel s.p.’s total
					liability exceed the amount you paid us, if any, in the 3 months preceding the event
					giving rise to the liability or one hundred US dollars (USD $100), whichever is greater.
				</li>
				<li>
					<strong>No Liability for GitHub Repositories</strong> You acknowledge that integrating
					your GitHub account with CodeCannon may result in information within your linked
					repositories being overwritten, modified, or damaged. CodeCannon disclaims all liability
					for any such loss or alteration. You configure and use any GitHub integrations at your own
					sole risk.
				</li>
			</ol>
		</section>

		<section id="indemnification">
			<h2>11. Indemnification</h2>
			<p>
				You agree to defend, indemnify, and hold harmless Andrej Fidel s.p. from and against all
				claims, damages, liabilities, losses, costs, and expenses (including reasonable attorneys’
				fees) arising out of or in any way connected with:
			</p>
			<ul>
				<li>Your use of Codecannon or any activities under your account;</li>
				<li>Your breach or alleged breach of these Terms;</li>
				<li>Your violation of any law or the rights of a third party.</li>
			</ul>
		</section>

		<section id="third-party">
			<h2>12. Third-Party Services</h2>
			<p>
				Codecannon may integrate, incorporate, or otherwise link to third-party services or content.
				We do not endorse or assume responsibility for these external services. While your direct
				interactions with such third-party services are governed by their own terms and policies,
				you can find details about the third-party services we use and how we handle data in
				connection with them in our Privacy Policy.
			</p>
		</section>

		<section id="termination">
			<h2>13. Term and Termination</h2>
			<h3>Term</h3>
			<p>These Terms remain in effect as long as you use Codecannon.</p>
			<h3>Termination by You</h3>
			<p>You may stop using Codecannon at any time.</p>
			<h3>Termination by Us</h3>
			<p>
				We may suspend or terminate your access to Codecannon at any time for any reason, including
				but not limited to violation of these Terms.
			</p>
			<h3>Effect of Termination</h3>
			<p>
				Upon termination, your rights to use Codecannon will immediately cease, and you must
				discontinue all use of the Service.
			</p>
		</section>

		<section id="governing-law">
			<h2>14. Governing Law and Dispute Resolution</h2>
			<p>
				These Terms and your use of Codecannon will be governed by and construed in accordance with
				the laws of the jurisdiction where Andrej Fidel s.p. is incorporated, without regard to its
				conflict of law provisions. Any disputes arising out of or in connection with these Terms
				will be resolved exclusively in the courts located in that jurisdiction, unless otherwise
				required by applicable law.
			</p>
		</section>

		<section id="changes-service">
			<h2>15. Changes to the Service</h2>
			<p>
				We reserve the right to modify, suspend, or discontinue Codecannon (in whole or in part) at
				any time, with or without notice to you. We will not be liable to you or any third party for
				any such modifications, suspension, or discontinuation.
			</p>
		</section>

		<section id="general">
			<h2>16. General</h2>
			<h3>Severability</h3>
			<p>
				If any provision of these Terms is found to be invalid or unenforceable, the remaining
				provisions will continue in full force and effect.
			</p>
			<h3>No Waiver</h3>
			<p>
				Any failure by us to enforce any right or provision under these Terms does not constitute a
				waiver of future enforcement of that right or provision.
			</p>
			<h3>Entire Agreement</h3>
			<p>
				These Terms, along with any other agreements referenced herein (including our Privacy
				Policy), constitute the entire agreement between you and Andrej Fidel s.p. regarding your
				use of Codecannon.
			</p>
			<h3>Assignment</h3>
			<p>
				You may not assign or transfer these Terms or any rights or obligations herein without our
				prior written consent. We may freely assign or transfer these Terms.
			</p>
		</section>

		<footer>&copy; 2025 Andrej Fidel s.p. All rights reserved.</footer>
	</div>
</template>
<script setup lang="ts"></script>

<style scoped>
.container {
	max-width: 800px;
	margin: 2rem auto;
	padding: 1rem;
	line-height: 1.6;
}
h1 {
	font-size: 2rem;
	margin-bottom: 0.5rem;
	border-bottom: 2px solid #eee;
	padding-bottom: 0.5rem;
}
h2 {
	font-size: 1.5rem;
	margin-top: 1.5rem;
	margin-bottom: 0.5rem;
}
h3 {
	font-size: 1.25rem;
	margin-top: 1rem;
	margin-bottom: 0.5rem;
}
p {
	margin-bottom: 1rem;
}
ol,
ul {
	margin: 0 0 1rem 1.5rem;
}
li {
	margin-bottom: 0.5rem;
}
footer {
	text-align: center;
	font-size: 0.875rem;
	margin-top: 2rem;
	padding-top: 1rem;
	border-top: 1px solid #eee;
}
@media (max-width: 500px) {
	.container {
		margin: 1rem;
		padding: 0.75rem;
	}
}
</style>
